import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import apiService from '../services/api';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import Modal from '../components/Modal';
import CreatePersonWizard from '../components/equipe/CreatePersonWizard';
import PrestationsBulkModal from '../components/equipe/PrestationsBulkModal';
import TeamUniverseNav from '../components/equipe/TeamUniverseNav';
import TeamMemberLargeDrawer from '../components/equipe/TeamMemberLargeDrawer';
import UpcomingAbsences from '../components/equipe/UpcomingAbsences';
import RdvDetailContent from '../components/rendezVous/RdvDetailContent';
import { getRdvModalTitle } from '../utils/rdvStatusUx';
import TeamWeekPlanning from '../components/equipe/TeamWeekPlanning';
import TeamMonthPlanningGrid from '../components/equipe/TeamMonthPlanningGrid';
import EquipePlanningDateDetail from '../components/equipe/EquipePlanningDateDetail';
import { MemberListRow, TeamSummaryCards } from '../components/equipe/equipeUi';
import { countOperationalRdvOnDay, mapRdvToCommande, RDV_STATUS_UX } from '../utils/equipeRdvRules';
import { getParisMonthMeta, getParisWeekApiRange } from '../utils/equipeWeekParis';
import { mapInstructeurHoraireExceptionsResponse } from '../utils/equipeDateHoraireExceptions';
import {
  createMemberDetailLoadGate,
  shouldUpdateMemberSearchParam,
} from '../utils/equipeMemberLoadGate';
import { getOperationalCommercantId } from '../utils/operationalCommercantScope';

function syncFormsFromMember(m) {
  return {
    general: {
      prenom: m.prenom || '',
      nom: m.nom || '',
      fonctionMetier: m.fonctionMetier || '',
      telephone: m.telephone || '',
      email: m.email || '',
      actif: m.actif !== false,
    },
    admin: {
      hasAdminAccess: Boolean(m.hasAdminAccess),
      roleInterne: m.roleInterne || 'VENDEUR',
      adminEmail: m.email || '',
    },
  };
}

export default function Equipe() {
  const [searchParams, setSearchParams] = useSearchParams();
  const legacyFrom = searchParams.get('legacy');
  const memberFromUrl = searchParams.get('member');

  const [members, setMembers] = useState([]);
  const [absencesToday, setAbsencesToday] = useState(0);
  const [upcomingAbsences, setUpcomingAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [gestionPlanningView, setGestionPlanningView] = useState('semaine');
  const [planningDateDetail, setPlanningDateDetail] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  const [showWizard, setShowWizard] = useState(false);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoSaving, setPhotoSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const [prestationsPersonnel, setPrestationsPersonnel] = useState([]);
  const [rdvList, setRdvList] = useState([]);
  const [horairesByInstructeur, setHorairesByInstructeur] = useState({});
  const [absencesByInstructeur, setAbsencesByInstructeur] = useState({});
  const [dateExceptionsByInstructeur, setDateExceptionsByInstructeur] = useState({});
  const [weekLoading, setWeekLoading] = useState(false);

  const [selectedRdv, setSelectedRdv] = useState(null);
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false);

  const [generalForm, setGeneralForm] = useState({
    prenom: '',
    nom: '',
    fonctionMetier: '',
    telephone: '',
    email: '',
    actif: true,
  });
  const [adminForm, setAdminForm] = useState({
    hasAdminAccess: false,
    roleInterne: 'VENDEUR',
    adminEmail: '',
  });

  const { toasts, toast, removeToast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const memberLoadGateRef = useRef(createMemberDetailLoadGate());

  const commercantId = getOperationalCommercantId();

  const prestataires = useMemo(
    () => (members || []).filter((m) => m.isIntervenant && m.instructeurId),
    [members],
  );

  const fetchEquipe = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getEquipe(commercantId);
      setMembers(data.members || []);
      setAbsencesToday(data.absencesToday ?? 0);
      setUpcomingAbsences(data.upcomingAbsences || []);
      return data.members || [];
    } catch (err) {
      setError(err.data?.error?.message || err.message || 'Erreur lors du chargement');
      return [];
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  const loadMemberDetail = useCallback(
    async (memberId) => {
      if (!memberId) {
        setMemberDetail(null);
        return;
      }
      try {
        const data = await apiService.getEquipeMember(memberId, commercantId);
        setMemberDetail(data.member);
        const forms = syncFormsFromMember(data.member);
        setGeneralForm(forms.general);
        setAdminForm(forms.admin);
      } catch (err) {
        memberLoadGateRef.current.reset();
        toastRef.current.error(err.data?.error?.message || err.message || 'Membre introuvable');
        setSelectedId(null);
        setMemberDetail(null);
      }
    },
    [commercantId],
  );

  const loadWeekData = useCallback(async () => {
    if (!commercantId) return;
    const ids = prestataires.map((m) => m.instructeurId).filter(Boolean);
    if (!ids.length) {
      setRdvList([]);
      setHorairesByInstructeur({});
      setAbsencesByInstructeur({});
      setDateExceptionsByInstructeur({});
      return;
    }

    setWeekLoading(true);
    try {
      const useMonth = gestionPlanningView === 'mois';
      const monthMeta = getParisMonthMeta(monthOffset);
      const weekRange = getParisWeekApiRange(weekOffset);
      const dateMin = useMonth ? monthMeta.dateMin : weekRange.dateMin;
      const dateMax = useMonth ? monthMeta.dateMax : weekRange.dateMax;
      const from = useMonth ? monthMeta.firstYmd : weekRange.days[0].ymd;
      const to = useMonth ? monthMeta.lastYmd : weekRange.days[6].ymd;
      const [allRdv, ...perInstructeur] = await Promise.all([
        apiService.getRendezVousByCommercantAll(commercantId, { dateMin, dateMax }),
        ...ids.flatMap((id) => [
          apiService.getInstructeurHorairesConfig(id),
          apiService.getInstructeurIndisponibilites(id, { actif: true }),
          apiService.getInstructeurHoraireDateExceptions(id, { from, to }),
        ]),
      ]);

      setRdvList(allRdv);

      const horairesMap = {};
      const absencesMap = {};
      const exceptionsMap = {};
      for (let i = 0; i < ids.length; i++) {
        const instructeurId = ids[i];
        horairesMap[instructeurId] = perInstructeur[i * 3];
        absencesMap[instructeurId] = perInstructeur[i * 3 + 1]?.indisponibilites || [];
        exceptionsMap[instructeurId] = mapInstructeurHoraireExceptionsResponse(perInstructeur[i * 3 + 2]);
      }
      setHorairesByInstructeur(horairesMap);
      setAbsencesByInstructeur(absencesMap);
      setDateExceptionsByInstructeur(exceptionsMap);
    } catch (err) {
      toastRef.current.error(err.message || 'Erreur chargement planning semaine');
    } finally {
      setWeekLoading(false);
    }
  }, [commercantId, prestataires, weekOffset, monthOffset, gestionPlanningView]);

  const refreshInstructeurPlanning = useCallback(
    async (instructeurId) => {
      if (!instructeurId || !commercantId) return;
      try {
        const [config, indispoData, equipeData] = await Promise.all([
          apiService.getInstructeurHorairesConfig(instructeurId),
          apiService.getInstructeurIndisponibilites(instructeurId, { actif: true }),
          apiService.getEquipe(commercantId),
        ]);
        setHorairesByInstructeur((prev) => ({ ...prev, [instructeurId]: config }));
        setAbsencesByInstructeur((prev) => ({
          ...prev,
          [instructeurId]: indispoData.indisponibilites || [],
        }));
        setAbsencesToday(equipeData.absencesToday ?? 0);
        setUpcomingAbsences(equipeData.upcomingAbsences || []);
      } catch (err) {
        toastRef.current.error(err.message || 'Erreur rafraîchissement planning');
      }
    },
    [commercantId],
  );

  useEffect(() => {
    fetchEquipe();
  }, [fetchEquipe]);

  useEffect(() => {
    if (!commercantId) return;
    apiService
      .getPrestationsPersonnelForInstructeurs(commercantId)
      .then((d) => setPrestationsPersonnel(d.prestations || []))
      .catch(() => setPrestationsPersonnel([]));
  }, [commercantId]);

  useEffect(() => {
    loadWeekData();
  }, [loadWeekData]);

  useEffect(() => {
    if (memberFromUrl && members.length) {
      const exists = members.find((m) => m.id === memberFromUrl);
      if (exists) {
        setSelectedId(memberFromUrl);
      }
    }
  }, [memberFromUrl, members]);

  // Charge la fiche une seule fois par (selectedId, commercantId) — anti-boucle LOT1.
  useEffect(() => {
    if (!selectedId) {
      memberLoadGateRef.current.reset();
      return;
    }

    const shouldFetch = memberLoadGateRef.current.shouldLoad(selectedId, commercantId);
    if (shouldFetch) {
      memberLoadGateRef.current.markLoaded(selectedId, commercantId);
      loadMemberDetail(selectedId);
    }

    setSearchParams(
      (prev) => {
        if (!shouldUpdateMemberSearchParam(prev.get('member'), selectedId)) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        next.set('member', selectedId);
        return next;
      },
      { replace: true },
    );
  }, [selectedId, commercantId, loadMemberDetail, setSearchParams]);

  const filtered = members.filter((m) => {
    if (filter === 'admin' && !m.hasAdminAccess) return false;
    if (filter === 'intervenant' && !m.isIntervenant) return false;
    if (filter === 'actif' && m.actif === false) return false;
    if (filter === 'inactif' && m.actif !== false) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      return (
        m.prenom?.toLowerCase().includes(t) ||
        m.nom?.toLowerCase().includes(t) ||
        m.fonctionMetier?.toLowerCase().includes(t) ||
        m.email?.toLowerCase().includes(t) ||
        m.telephone?.includes(t)
      );
    }
    return true;
  });

  const selectMember = (id, tab = 'general') => {
    setSelectedId(id);
    setActiveTab(tab);
    setMemberDrawerOpen(true);
    setPlanningDateDetail(null);
  };

  const handleGestionMonthCellClick = ({ member, day, instructeurId }) => {
    if (!member?.id || !day) return;
    setSelectedId(member.id);
    setPlanningDateDetail({ member, day, instructeurId });
    setMemberDrawerOpen(false);
  };

  const refreshMember = async (memberId) => {
    const list = await fetchEquipe();
    const targetId = memberId || selectedId;
    if (targetId && list.find((m) => m.id === targetId)) {
      setSelectedId(targetId);
      await loadMemberDetail(targetId);
    }
    await loadWeekData();
  };

  const handleCreate = async (payload) => {
    setWizardLoading(true);
    try {
      const result = await apiService.createEquipeMember(payload, commercantId);
      toast.success('Personne ajoutée');
      setShowWizard(false);
      if (result.password) {
        setTempPassword({ password: result.password, name: `${payload.prenom} ${payload.nom}` });
      }
      await fetchEquipe();
      if (result.memberId) {
        selectMember(result.memberId, 'general');
      }
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Création impossible');
    } finally {
      setWizardLoading(false);
    }
  };

  const savePractitionerPhoto = async (photoUrl) => {
    if (!selectedId) return;
    setPhotoSaving(true);
    try {
      const data = await apiService.updateEquipeMember(
        selectedId,
        { photo: photoUrl || null },
        commercantId,
      );
      toast.success(photoUrl ? 'Photo professionnelle enregistrée' : 'Photo professionnelle supprimée');
      await refreshMember(data.memberId || selectedId);
      await fetchEquipe();
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Erreur photo');
    } finally {
      setPhotoSaving(false);
    }
  };

  const saveGeneral = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = {
        prenom: generalForm.prenom.trim(),
        nom: generalForm.nom.trim(),
        telephone: generalForm.telephone.trim() || null,
        fonctionMetier: generalForm.fonctionMetier.trim() || null,
      };
      if (!memberDetail?.hasAdminAccess && memberDetail?.kind === 'INSTRUCTEUR_ONLY') {
        payload.email = generalForm.email.trim() || null;
      }
      const protections = memberDetail?.protections || {};
      // Intervenant (Instructeur.*) ≠ compte Clickorico (User.statut).
      const hasInstructeurProfile = Boolean(
        memberDetail?.instructeurId ||
          memberDetail?.instructeur?.id ||
          memberDetail?.kind === 'INSTRUCTEUR_ONLY',
      );
      if (hasInstructeurProfile && !protections.isSelf) {
        payload.intervenantActif = Boolean(generalForm.actif);
      } else if (!hasInstructeurProfile && !protections.isSelf && !protections.isGerant) {
        payload.statut = generalForm.actif ? 'ACTIF' : 'INACTIF';
      }
      const data = await apiService.updateEquipeMember(selectedId, payload, commercantId);
      toast.success('Enregistré');
      await refreshMember(data.memberId || selectedId);
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const savePrestationCapability = async (value) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const data = await apiService.updateEquipeMember(
        selectedId,
        { isIntervenant: value },
        commercantId,
      );
      toast.success('Capacité prestations mise à jour');
      await refreshMember(data.memberId || selectedId);
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const saveAdminAccess = async () => {
    if (!selectedId || !memberDetail) return;

    setSaving(true);
    try {
      const payload = {};
      if (!memberDetail.hasAdminAccess && adminForm.hasAdminAccess) {
        payload.hasAdminAccess = true;
        payload.email = adminForm.adminEmail.trim();
        payload.roleInterne = adminForm.roleInterne;
      } else if (memberDetail.hasAdminAccess && !adminForm.hasAdminAccess) {
        payload.hasAdminAccess = false;
      } else if (memberDetail.hasAdminAccess && canManageRoles) {
        payload.roleInterne = adminForm.roleInterne;
      }

      const data = await apiService.updateEquipeMember(selectedId, payload, commercantId);
      if (data.password) {
        setTempPassword({
          password: data.password,
          name: `${memberDetail.prenom} ${memberDetail.nom}`,
        });
      }
      toast.success('Accès enregistré');
      await refreshMember(data.memberId || selectedId);
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async (prestationIds) => {
    if (!selectedId) return;
    setBulkSaving(true);
    try {
      const data = await apiService.setEquipePrestations(selectedId, prestationIds, commercantId);
      toast.success('Prestations enregistrées');
      setShowBulk(false);
      setMemberDetail(data.member);
      await fetchEquipe();
    } catch (err) {
      toast.error(err.data?.error?.message || err.message || 'Erreur');
    } finally {
      setBulkSaving(false);
    }
  };


  const canManageRoles = memberDetail?.protections?.canChangeRole !== false;
  const instructeurId = memberDetail?.instructeurId || memberDetail?.instructeur?.id;
  const memberAbsences = instructeurId ? absencesByInstructeur[instructeurId] || [] : [];
  const memberRdvList = instructeurId
    ? (rdvList || []).filter((r) => r.instructeurId === instructeurId)
    : [];

  const gestionMonthMeta = useMemo(() => getParisMonthMeta(monthOffset), [monthOffset]);
  const planningDetailInstructeurId = planningDateDetail?.instructeurId;
  const planningDetailRdvCount = useMemo(() => {
    if (!planningDateDetail?.day?.ymd || !planningDetailInstructeurId) return 0;
    const forInstructeur = (rdvList || []).filter(
      (r) => r.instructeurId === planningDetailInstructeurId,
    );
    return countOperationalRdvOnDay(forInstructeur, planningDateDetail.day.ymd);
  }, [rdvList, planningDateDetail, planningDetailInstructeurId]);

  if (loading && !members.length) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}

      {legacyFrom && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {legacyFrom === 'praticiens'
            ? 'Les praticiens sont désormais gérés depuis Mon équipe.'
            : 'Mon équipe remplace l’ancienne page Staff.'}
        </div>
      )}

      <TeamUniverseNav
        subtitle="Gérez les personnes de votre commerce, leurs prestations et leurs accès."
        actions={
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4" />
            Ajouter une personne
          </button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <TeamSummaryCards members={members} absencesToday={absencesToday} />

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher une personne…"
            className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm shadow-sm"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm shadow-sm"
        >
          <option value="all">Tous</option>
          <option value="admin">Accès Clickorico</option>
          <option value="intervenant">Réalisent des prestations</option>
          <option value="actif">Actifs</option>
          <option value="inactif">Inactifs</option>
        </select>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(220px,25%)_minmax(0,1fr)] lg:items-start">
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm">
            <h2 className="mb-3 px-1 text-sm font-bold text-gray-900">
              Membres de l&apos;équipe
              <span className="ml-2 text-xs font-normal text-gray-400">({filtered.length})</span>
            </h2>
            <div className="max-h-[min(420px,50vh)] space-y-2 overflow-y-auto pr-0.5">
              {filtered.length === 0 ? (
                <p className="px-1 py-4 text-center text-sm text-gray-500">Aucune personne trouvée.</p>
              ) : (
                filtered.map((m) => (
                  <MemberListRow
                    key={m.id}
                    member={m}
                    compact
                    selected={selectedId === m.id}
                    onClick={() => selectMember(m.id)}
                  />
                ))
              )}
            </div>
          </div>
          <UpcomingAbsences
            absences={upcomingAbsences}
            members={members}
            onSelectMember={selectMember}
          />
        </div>

        <div className="flex min-h-[400px] flex-col gap-2">
          <div className="flex justify-end">
            <div className="flex items-center rounded-xl bg-gray-100 p-1 shadow-sm">
              {[
                { id: 'semaine', label: 'Semaine' },
                { id: 'mois', label: 'Mois' },
              ].map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setGestionPlanningView(v.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    gestionPlanningView === v.id
                      ? 'bg-white text-violet-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {gestionPlanningView === 'semaine' ? (
            <TeamWeekPlanning
              members={members}
              selectedId={selectedId}
              onSelectMember={selectMember}
              weekOffset={weekOffset}
              onWeekOffsetChange={setWeekOffset}
              horairesByInstructeur={horairesByInstructeur}
              absencesByInstructeur={absencesByInstructeur}
              dateExceptionsByInstructeur={dateExceptionsByInstructeur}
              rdvList={rdvList}
              loading={weekLoading}
              onCellClick={handleGestionMonthCellClick}
              hideSemaineBadge
              hideTodayButton
              title="Planning équipe"
              subtitle="Vision collective de la semaine — clic sur un nom pour la fiche complète"
            />
          ) : (
            <div className="flex flex-1 flex-col rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
                <div>
                  <h3 className="text-base font-bold text-gray-900">Planning équipe</h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Vue mois — clic sur une cellule pour le détail du jour
                  </p>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setMonthOffset((o) => o - 1)}
                      className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
                      aria-label="Mois précédent"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="min-w-[9rem] text-center text-sm font-semibold text-gray-800">
                      {gestionMonthMeta.label}
                    </span>
                    <button
                      type="button"
                      onClick={() => setMonthOffset((o) => o + 1)}
                      className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
                      aria-label="Mois suivant"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  {monthOffset !== 0 ? (
                    <button
                      type="button"
                      onClick={() => setMonthOffset(0)}
                      className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100"
                    >
                      Ce mois-ci
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="min-h-0 flex-1 p-3 sm:p-4">
                <TeamMonthPlanningGrid
                  members={members}
                  monthDays={gestionMonthMeta.days}
                  selectedId={selectedId}
                  onSelectMember={selectMember}
                  horairesByInstructeur={horairesByInstructeur}
                  absencesByInstructeur={absencesByInstructeur}
                  dateExceptionsByInstructeur={dateExceptionsByInstructeur}
                  rdvList={rdvList}
                  loading={weekLoading}
                  onCellClick={handleGestionMonthCellClick}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <EquipePlanningDateDetail
        open={Boolean(planningDateDetail)}
        onClose={() => setPlanningDateDetail(null)}
        member={planningDateDetail?.member}
        day={planningDateDetail?.day}
        horairesConfig={
          planningDetailInstructeurId
            ? horairesByInstructeur[planningDetailInstructeurId]
            : null
        }
        absences={
          planningDetailInstructeurId
            ? absencesByInstructeur[planningDetailInstructeurId] || []
            : []
        }
        dateExceptionsByDate={
          planningDetailInstructeurId
            ? dateExceptionsByInstructeur[planningDetailInstructeurId] || null
            : null
        }
        rdvCount={planningDetailRdvCount}
        onSaved={loadWeekData}
      />

      <TeamMemberLargeDrawer
        open={memberDrawerOpen && Boolean(memberDetail)}
        onClose={() => setMemberDrawerOpen(false)}
        panelProps={{
          member: memberDetail,
          activeTab,
          onTabChange: setActiveTab,
          generalForm,
          onGeneralFormChange: setGeneralForm,
          adminForm,
          onAdminFormChange: setAdminForm,
          onSaveGeneral: saveGeneral,
          commercantId,
          onPractitionerPhotoChange: savePractitionerPhoto,
          photoSaving,
          onSaveAdmin: saveAdminAccess,
          onSavePrestationCapability: savePrestationCapability,
          onManagePrestations: () => setShowBulk(true),
          prestations: prestationsPersonnel,
          saving,
          canManageRoles,
          weekOffset,
          onWeekOffsetChange: setWeekOffset,
          horairesConfig: instructeurId ? horairesByInstructeur[instructeurId] : null,
          absences: memberAbsences,
          rdvList: memberRdvList,
          weekLoading,
          onRdvClick: setSelectedRdv,
          onPlanningChanged: refreshInstructeurPlanning,
        }}
      />

      <CreatePersonWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSubmit={handleCreate}
        loading={wizardLoading}
      />

      <PrestationsBulkModal
        isOpen={showBulk}
        onClose={() => setShowBulk(false)}
        prestations={prestationsPersonnel}
        initialSelectedIds={memberDetail?.instructeur?.prestationIds || []}
        onSave={handleBulkSave}
        saving={bulkSaving}
      />

      <Modal
        isOpen={Boolean(tempPassword)}
        onClose={() => setTempPassword(null)}
        title="Mot de passe généré"
      >
        {tempPassword && (
          <div className="space-y-3 text-sm">
            <p>
              Compte créé pour <strong>{tempPassword.name}</strong>.
            </p>
            <p className="rounded-lg bg-amber-50 px-3 py-2 font-mono text-amber-900">
              {tempPassword.password}
            </p>
            <p className="text-xs text-gray-500">Ce mot de passe ne sera affiché qu’une seule fois.</p>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(selectedRdv)}
        onClose={() => setSelectedRdv(null)}
        title={getRdvModalTitle(mapRdvToCommande(selectedRdv))}
        maxWidth="max-w-2xl"
      >
        {selectedRdv && (
          <RdvDetailContent
            commande={mapRdvToCommande(selectedRdv)}
            statusDisplay={RDV_STATUS_UX[selectedRdv.statut] || RDV_STATUS_UX.EN_ATTENTE}
            showCommercant
          />
        )}
      </Modal>
    </div>
  );
}
